import { useMutation } from '@apollo/client';
import { endExperimentMutation } from 'api/mutations/experiments';
import { analyticsBasePageData } from 'modules/experiments/ducks/actions';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

type CallbackFn = () => void;

type EndExperiment = {
  id: number;
  winningTemplateId?: number;
  /** Do the same action whether the operation is a success or not */
  onComplete?: CallbackFn;
  onSuccess?: CallbackFn;
  onError?: CallbackFn;
};

const source = 'useEndExperiment';

export const useEndExperiment = ({
  id,
  winningTemplateId,
  onComplete,
  onSuccess,
  onError,
}: EndExperiment) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const onSuccessCallback = onSuccess || onComplete;
  const onErrorCallback = onError || onComplete;
  const errorMessage = winningTemplateId
    ? `Failed to end experiment id ${id}. And set ${winningTemplateId} as the winning variant id`
    : `Failed to end experiment id ${id}`;
  const errorToasterMessage = winningTemplateId
    ? t('errorMessage.declareWinner')
    : t('errorMessage.endExperiment');
  const successToasterMessage = winningTemplateId
    ? t('successMessage.declareWinner')
    : t('successMessage.endExperiment');

  const errorHandler = (error: unknown) => {
    if (onErrorCallback) onErrorCallback();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [endExperiment] = useMutation(endExperimentMutation, {
    onError: errorHandler,
  });

  return async () => {
    const { data } = await endExperiment({
      variables: { id, winnerId: winningTemplateId },
    });

    if (data?.endExperiment?.id) {
      if (onSuccessCallback) onSuccessCallback();
      dispatch(analyticsBasePageData(data.endExperiment));
      dispatch(addPositiveToast(successToasterMessage));
    }

    if (data?.endExperiment?.message) {
      errorHandler(data.endExperiment.message);
    }
  };
};
